import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { requestPresignedDownload } from '../../api/files';
import { replaceWorkspaceFileText } from '../../api/workspace';
import { invalidateFileBytes } from '../../lib/file-bytes-cache';
import { tWorkspace } from '../../lib/workspace-i18n';

/**
 * 일반 텍스트 편집기 (2026-08-14).
 *
 * `＋ 만들기 → 텍스트 문서(.txt)` 는 파일을 만들어 주고도 **열 방법이 없었다** — 미리보기
 * 판정(`previewKindOfNode`)에 텍스트가 없어 "이 형식은 미리보기를 지원하지 않습니다" 로
 * 끝났다. 만들자마자 막다른 길이 되는 셈이라 여기서 닫는다.
 *
 * ⚠ 마크다운 편집기를 재사용하지 않는 이유.
 *   저장 경로(presigned-replace)는 같아서 붙이기는 쉬웠다. 그런데 그 편집기는 WYSIWYG 라
 *   저장할 때 `getMarkdown()` 으로 **다시 직렬화**한다. 마크다운 문법이 아닌 평문(들여쓰기,
 *   `#` 로 시작하는 주석, `1.` 로 시작하는 줄, 표처럼 생긴 정렬)이 그 왕복에서 이스케이프
 *   되거나 재배치될 수 있다. 로그·CSV·설정 파일을 그렇게 조용히 바꾸면 안 된다.
 *   평문은 평문으로 — 받은 바이트를 그대로 보여 주고 그대로 돌려보낸다.
 */

export interface WorkspaceTextEditorHandle {
  /** 부모(문서 패널 헤더)의 저장 버튼이 부른다. */
  save: () => Promise<void>;
  /**
   * 지금 편집 중인 글 — AI 카드가 문서 컨텍스트로 쓴다 (2026-08-15, PR3).
   *
   * ⚠ **저장된 내용이 아니라 화면의 내용**이다. 사용자가 방금 고친 문장을 두고 물었는데
   *   AI 가 저장본을 보고 답하면 대화가 어긋난다.
   */
  getText: () => string | null;
  /**
   * 커서 자리에 글을 넣는다 — AI 답변의 "편집기에 삽입" (PR3).
   *
   * 아직 파일을 못 읽었으면 `false`(넣을 자리가 없다). 성공하면 미저장 표시가 켜진다.
   */
  insertText: (text: string) => boolean;
  /**
   * 글 전체를 갈아 끼운다 — AI 도구가 문서를 고칠 때 쓴다 (2026-08-15, Phase B).
   *
   * ⚠ `insertText` 와 달리 **커서를 옮기지 않고 스크롤 자리를 지킨다.** 문서 전체가 바뀌는
   *   편집이라 커서를 따라가게 두면 화면이 튀어, 사용자가 방금 무엇이 바뀌었는지 못 본다.
   */
  setText: (text: string) => boolean;
  /**
   * 글자 구간 `[start, end)` 를 갈아 끼운다 (2026-08-15, Phase B).
   *
   * 범위는 글 길이로 조여서 받는다 — 모델이 계산을 조금 틀려도 예외로 죽지 않고
   * 가장 가까운 유효 구간에 적용된다.
   */
  replaceRange: (start: number, end: number, text: string) => boolean;
}

interface Props {
  fileId: string;
  /** 미저장 표시(제목의 •)와 닫기 가드는 패널이 소유한다. */
  onDirtyChange: (dirty: boolean) => void;
  /** 저장 성공 — 목록의 크기 갱신용. */
  onSaved: () => void;
  onError: (message: string) => void;
}

export const WorkspaceTextEditor = forwardRef<WorkspaceTextEditorHandle, Props>(
  function WorkspaceTextEditor({ fileId, onDirtyChange, onSaved, onError }, ref) {
    const [text, setText] = useState<string | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    /** 마지막으로 저장(또는 로드)된 내용 — dirty 판정 기준. */
    const savedRef = useRef<string>('');
    const textRef = useRef<string>('');
    /**
     * 삽입 위치(커서)를 알기 위한 참조 (2026-08-15, PR3).
     *
     * ⚠ `document.activeElement` 로 대신할 수 없다 — AI 카드의 버튼을 누르는 순간
     *   포커스는 그 버튼에 있고, textarea 의 `selectionStart` 는 **포커스를 잃어도 남는다**.
     */
    const areaRef = useRef<HTMLTextAreaElement | null>(null);
    /** 삽입 뒤 커서를 놓을 자리 — 다시 그려진 뒤에 적용해야 한다(값 갱신이 커서를 끝으로 민다). */
    const caretRef = useRef<number | null>(null);
    /** AI 편집 뒤 되돌려 놓을 스크롤 자리 — 문서 전체가 바뀌면 브라우저가 맨 위로 되감는다. */
    const keepScrollRef = useRef<number | null>(null);

    // 콜백은 부모가 인라인 화살표로 넘길 수 있어 deps 에 넣으면 매 렌더 재로딩이 된다.
    // 편집 중인 내용을 지우는 최악의 회귀라 ref 로 최신 값만 들고 간다(문서 패널과 같은 규약).
    const onDirtyRef = useRef(onDirtyChange);
    onDirtyRef.current = onDirtyChange;
    const onSavedRef = useRef(onSaved);
    onSavedRef.current = onSaved;
    const onErrorRef = useRef(onError);
    onErrorRef.current = onError;

    useEffect(() => {
      let cancelled = false;
      setText(null);
      setLoadError(null);
      void (async () => {
        try {
          const res = await requestPresignedDownload(fileId, { purpose: 'preview' }); // 인앱 편집기로 읽기 = 「열람」(§3.4.1 · 기기에 저장 안 함)
          const r = await fetch(res.downloadUrl);
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          const t = await r.text();
          if (cancelled) return;
          savedRef.current = t;
          textRef.current = t;
          setText(t);
          onDirtyRef.current(false);
        } catch (err) {
          if (!cancelled) setLoadError((err as Error)?.message ?? 'error');
        }
      })();
      return () => { cancelled = true; };
    }, [fileId]);

    useImperativeHandle(ref, () => ({
      async save() {
        // 아직 못 읽은 파일을 저장하면 **빈 내용으로 덮어쓴다**. 로드 전에는 아무것도 하지 않는다.
        if (text === null) return;
        try {
          await replaceWorkspaceFileText(fileId, textRef.current);
          savedRef.current = textRef.current;
          // ⚠ 내용이 바뀌었으므로 공유 바이트 캐시를 버린다 — 안 그러면 다시 열 때
          //   **저장 전 바이트**가 나온다(오피스 편집기와 같은 규약).
          invalidateFileBytes(fileId);
          onDirtyRef.current(false);
          onSavedRef.current();
        } catch (err) {
          const message = (err as any)?.response?.data?.message
            ?? (err as Error)?.message
            ?? tWorkspace('workspace.editor.text_save_failed');
          onErrorRef.current(message);
        }
      },
      getText: () => (text === null ? null : textRef.current),
      insertText(value) {
        // 아직 못 읽은 파일에 넣으면 로드가 끝나는 순간 덮어써진다 — 넣을 자리가 없다.
        if (text === null || !value) return false;
        const el = areaRef.current;
        const cur = textRef.current;
        // 커서를 모르면 **끝에** 붙인다. 문서 앞을 조용히 헤집는 것보다 낫다.
        const start = el ? el.selectionStart ?? cur.length : cur.length;
        const end = el ? el.selectionEnd ?? start : start;
        const next = cur.slice(0, start) + value + cur.slice(end);
        caretRef.current = start + value.length;
        textRef.current = next;
        setText(next);
        onDirtyRef.current(next !== savedRef.current);
        return true;
      },
      setText(value) {
        if (text === null || typeof value !== 'string') return false;
        // 커서(caretRef)를 건드리지 않는다 — 대신 보고 있던 자리를 지킨다.
        keepScrollRef.current = areaRef.current?.scrollTop ?? null;
        textRef.current = value;
        setText(value);
        onDirtyRef.current(value !== savedRef.current);
        return true;
      },
      replaceRange(start, end, value) {
        if (text === null || typeof value !== 'string') return false;
        const cur = textRef.current;
        const s = Math.max(0, Math.min(Math.trunc(start), cur.length));
        const e = Math.max(s, Math.min(Math.trunc(end), cur.length));
        const next = cur.slice(0, s) + value + cur.slice(e);
        keepScrollRef.current = areaRef.current?.scrollTop ?? null;
        textRef.current = next;
        setText(next);
        onDirtyRef.current(next !== savedRef.current);
        return true;
      },
    }), [fileId, text]);

    // 삽입으로 값을 바꾼 다음 프레임에 커서를 넣은 글 뒤로 옮긴다 — 안 하면 커서가 끝으로
    // 튀어, 이어서 타자를 치면 문서 맨 뒤에 적힌다.
    useEffect(() => {
      const caret = caretRef.current;
      if (caret === null) return;
      caretRef.current = null;
      const el = areaRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(caret, caret);
    }, [text]);

    // AI 가 글을 갈아 끼우면 값 갱신이 스크롤을 맨 위로 되감는다 — 보고 있던 자리로 돌려놓는다.
    // (포커스는 건드리지 않는다. 사용자가 챗박스에 글을 쓰던 중일 수 있다.)
    useEffect(() => {
      const top = keepScrollRef.current;
      if (top === null) return;
      keepScrollRef.current = null;
      const el = areaRef.current;
      if (el) el.scrollTop = top;
    }, [text]);

    if (loadError) {
      return <div className="workspace-docpanel-status" role="status">{loadError}</div>;
    }
    if (text === null) {
      return (
        <div className="workspace-docpanel-status" role="status">
          {tWorkspace('workspace.grid.loading')}
        </div>
      );
    }

    return (
      <textarea
        ref={areaRef}
        className="workspace-docpanel-textarea"
        aria-label={tWorkspace('workspace.editor.text_area')}
        spellCheck={false}
        value={text}
        onChange={(e) => {
          const next = e.target.value;
          textRef.current = next;
          setText(next);
          onDirtyRef.current(next !== savedRef.current);
        }}
      />
    );
  },
);
